import { apiFetch } from './auth';

export function listTrucks() {
  return apiFetch('/trucks');
}

export function createTruck(data) {
  return apiFetch('/trucks', { method: 'POST', body: JSON.stringify(data) });
}

export function updateTruck(id, data) {
  return apiFetch('/trucks/' + id, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteTruck(id) {
  return apiFetch('/trucks/' + id, { method: 'DELETE' });
}

// Single consolidated payload for the dashboard: trucks + telemetry + events.
// Replaces the old getDevices/getTelemetry/getHistory chain against ThingsBoard.
export function getFleetLive() {
  return apiFetch('/fleet/live');
}

// Active announcement banners for the caller's org (target=all or this org).
export function getAnnouncements() {
  return apiFetch('/announcements');
}

// The caller's org feature flags as a flat { flag: bool } map. Lets the app gate
// features (e.g. fuel_export, co2_reporting).
export function getMyFlags() {
  return apiFetch('/me/flags');
}

// Org-level sensor-health roll-up — BLE quality, battery voltage, GSM signal,
// GPS staleness, and a per-truck alert list. Trucks sorted critical first.
export function getFleetHealth() {
  return apiFetch('/fleet/health');
}

// Trucks that need refuelling, sorted critical → high → medium. Trucks with
// urgency 'ok' are omitted server-side so the dashboard card stays focused.
export function getRefuelSchedule() {
  return apiFetch('/fleet/refuel-schedule');
}

// Per-truck battery voltage history (last 30 days) + 7-day trend regression.
// Returns { samples: [{date, ext_mv, bat_mv}], trend: {...} | null }.
export function getTruckBattery(id) {
  return apiFetch('/trucks/' + id + '/battery');
}

// 24h timeseries for one truck, lazy-loaded when a truck is selected.
// Pass { date: 'YYYY-MM-DD' } for a specific UTC calendar day instead of the rolling window.
// Pass { keys: 'fuel_level_liters,...' } to restrict to a subset of telemetry keys.
// Returns { truck_id, tanks, date, hours, history: { ...keyed timeseries } }.
export function getTruckHistory(id, opts = {}) {
  const params = new URLSearchParams();
  if (opts.date) params.set('date', opts.date);
  if (opts.hours) params.set('hours', String(opts.hours));
  if (opts.keys) params.set('keys', opts.keys);
  const qs = params.toString();
  return apiFetch('/fleet/trucks/' + id + '/history' + (qs ? '?' + qs : ''));
}

// Detected trips for one truck. Defaults to last 24h; pass a date for a single
// calendar day. Each trip has a downsampled polyline so the replay map can
// render without a second history fetch.
export function getTruckTrips(id, date) {
  const qs = date ? '?date=' + encodeURIComponent(date) : '';
  return apiFetch('/fleet/trucks/' + id + '/trips' + qs);
}

// Per-truck fuel ledger (the diesel "bank statement"). hours defaults to 24
// and the server caps at 168 (one week). Returns { truck_id, summary, entries }.
export function getTruckLedger(id, hours = 24) {
  return apiFetch('/trucks/' + id + '/ledger?hours=' + hours);
}

// Fleet-wide ledger summary used by the morning view on Dashboard.
export function getFleetLedger(hours = 24) {
  return apiFetch('/fleet/ledger?hours=' + hours);
}

// Org-scoped users for the Settings → Team tab. Available to any authenticated user.
export function listTeam() {
  return apiFetch('/team');
}

// Billing / invoicing
export function listInvoices(orgId) {
  return apiFetch('/invoices' + (orgId ? '?org_id=' + orgId : ''));
}
export function getInvoice(id) {
  return apiFetch('/invoices/' + id);
}
export function generateInvoice(body) {
  return apiFetch('/invoices/generate', { method: 'POST', body: JSON.stringify(body) });
}
export function updateOrgBilling(orgId, body) {
  return apiFetch('/admin/organizations/' + orgId + '/billing', { method: 'PUT', body: JSON.stringify(body) });
}
export function listPlans() {
  return apiFetch('/billing/plans');
}
// Fire a synthetic theft alert through every configured channel — used by Settings → Alerts.
export function sendTestAlert() {
  return apiFetch('/alerts/test', { method: 'POST' });
}

// ── Customer alert workflow (migration 019) ─────────────────────────────────
// Org-scoped server-side; the caller only ever sees/mutates its own org's alerts.
export function listAlerts(opts = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.type) params.set('type', opts.type);
  if (opts.days) params.set('days', String(opts.days));
  const qs = params.toString();
  return apiFetch('/alerts' + (qs ? '?' + qs : ''));
}
export function getAlertCounts() {
  return apiFetch('/alerts/counts');
}
export function acknowledgeAlert(id) {
  return apiFetch('/alerts/' + id + '/acknowledge', { method: 'POST' });
}
export function resolveAlert(id, note) {
  return apiFetch('/alerts/' + id + '/resolve', { method: 'POST', body: JSON.stringify({ note: note || null }) });
}
export function reopenAlert(id) {
  return apiFetch('/alerts/' + id + '/reopen', { method: 'POST' });
}
export function assignAlert(id, userId) {
  return apiFetch('/alerts/' + id + '/assign', { method: 'POST', body: JSON.stringify({ user_id: userId ?? null }) });
}
// Mark a theft/tamper alert as a false alarm — suppresses it and feeds the
// classifier-tuning log (Phase 2). Optional free-text reason.
export function markAlertFalsePositive(id, reason) {
  return apiFetch('/alerts/' + id + '/false-positive', { method: 'POST', body: JSON.stringify({ reason: reason || null }) });
}

// Theft-evidence PDF for one alert. Fetched via the bearer-auth path (an <a href>
// can't reach it) and streamed to the browser as a download, mirroring
// downloadInvoicePdf. Returns nothing; throws on non-2xx.
export async function downloadAlertEvidence(id, label) {
  const base = (import.meta.env.VITE_API_URL || '/api') + '/alerts/' + id + '/evidence.pdf';
  const token = localStorage.getItem('tg_token');
  const res = await fetch(base, { headers: { Authorization: 'Bearer ' + token } });
  if (!res.ok) throw new Error('Failed to download evidence (' + res.status + ')');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (label || 'tankguard-evidence-' + id) + '.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

// Geofences
export function listGeofences() {
  return apiFetch('/geofences');
}
export function createGeofence(body) {
  return apiFetch('/geofences', { method: 'POST', body: JSON.stringify(body) });
}
export function updateGeofence(id, body) {
  return apiFetch('/geofences/' + id, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteGeofence(id) {
  return apiFetch('/geofences/' + id, { method: 'DELETE' });
}
export function listGeofenceEvents() {
  return apiFetch('/geofences/events');
}

// Webhooks — list + CRUD + send-test + delivery log.
export function listWebhooks() {
  return apiFetch('/webhooks');
}
export function createWebhook(body) {
  return apiFetch('/webhooks', { method: 'POST', body: JSON.stringify(body) });
}
export function updateWebhook(id, body) {
  return apiFetch('/webhooks/' + id, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteWebhook(id) {
  return apiFetch('/webhooks/' + id, { method: 'DELETE' });
}
export function testWebhook(id) {
  return apiFetch('/webhooks/' + id + '/test', { method: 'POST' });
}
export function listWebhookDeliveries(id, limit = 20) {
  return apiFetch('/webhooks/' + id + '/deliveries?limit=' + limit);
}

// Known locations registry — depots, customers, fuel stations. Auto-populated
// by refuel detection; admins can rename and reclassify via the UI.
export function getLocations() {
  return apiFetch('/locations');
}
export function createLocation(body) {
  return apiFetch('/locations', { method: 'POST', body: JSON.stringify(body) });
}
export function updateLocation(id, body) {
  return apiFetch('/locations/' + id, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteLocation(id) {
  return apiFetch('/locations/' + id, { method: 'DELETE' });
}

// Preview the weekly report for the caller's own org as a PDF blob download.
export async function downloadWeeklyReportPreview() {
  const base = (import.meta.env.VITE_API_URL || '/api') + '/reports/weekly/preview';
  const token = localStorage.getItem('tg_token');
  const res = await fetch(base, { headers: { Authorization: 'Bearer ' + token } });
  if (!res.ok) throw new Error('Preview failed (' + res.status + ')');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tankguard-weekly-preview.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

// Bearer auth lives in a JS-only header so <a href> cannot reach the PDF directly.
// downloadInvoicePdf fetches via apiFetch's auth path and serves the blob locally.
export async function downloadInvoicePdf(id, number) {
  const base = (import.meta.env.VITE_API_URL || '/api') + '/invoices/' + id + '/pdf';
  const token = localStorage.getItem('tg_token');
  const res = await fetch(base, { headers: { Authorization: 'Bearer ' + token } });
  if (!res.ok) throw new Error('Failed to download invoice (' + res.status + ')');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (number || 'invoice') + '.pdf';
  a.click();
  URL.revokeObjectURL(url);
}
